import { Grid, Tabs } from '@mui/material';
import { useEffect, useLayoutEffect, useState } from 'react';
import EstablishmentDataTabContent from '../modules/EstablishmentData';
import BackdropComponent from '../../../components/Backdrop/BackdropComponent';
import useCarcinogenicReportPrecharge from '../hooks/useCarcinogenicReportPrecharge';
import { useTranslation } from 'react-i18next';
import useTranslate from '../../../hooks/useTranslate';
import { CarcinogenicReportTab } from '../components/Tab';
import SubstancesAndPreventiveMeasuresTabContent from '../modules/SubstancesAndPreventiveMeasures';
import { useAppSelector } from '../../../hooks/reduxHooks';
import { useDispatch } from 'react-redux';
import {
    setCarcinogenicReporParametricData,
    setCarcinogenicReportCIIUCodes,
    setCarcinogenicReportCurrentTab
} from '../../../redux/slices/carcinogenicReportSlice';
import { useGetCarcinogenicParametricDataQuery } from '../../../services/carcinogenicApi';
import ProvidersTabContent from '../modules/Providers';
import RadiologicalEquipmentTabContent from '../modules/RadiologicalEquipment';
import WorkersAndMedicalExaminationsTabContent from '../modules/WorkersAndMedicalExaminations';
import { ResponsiblePartiesAndContractorsTabContent } from '../modules/ResponsiblePartiesAndContractors';
import { useGetCIIUCodesQuery } from '../../../services/companyApi';
import useCarcinogenicTabStates from '../hooks/useCarcinogenicTabStates';
import useAlerts from '../hooks/useAlerts';
import { TabPanel } from '../../../components/TabPanel';

export enum TAB_INDEX {
    ESTABLISHMENT_DATA = 0,
    SUBSTANCES = 1,
    PROVIDERS_AND_BUYERS = 2,
    RADIOLOGICAL_EQUIPMENT = 3,
    WORKERS_AND_MEDICAL_EXAMS = 4,
    RESPONSIBLES_AND_CONTRACTORS = 5
}

export type HandleTabChange = {
    (event: React.SyntheticEvent, newValue: TAB_INDEX): void;
    (newValue: TAB_INDEX): void;
};

const CarcinogenicReport = ({ onlyVisual = false }: { onlyVisual?: boolean }) => {
    const dispatch = useDispatch();
    const { data: ciiuCodes } = useGetCIIUCodesQuery(null);
    const {
        IS_ESTABLISHMENT_DATA_COMPLETE,
        IS_SUBSTANCES_DATA_COMPLETE,
        IS_PROVIDERS_AND_BUYERS_DATA_COMPLETE,
        IS_RADIOLOGICAL_EQUIPMENT_DATA_COMPLETE,
        IS_WORKERS_AND_MEDICAL_EXAMS_DATA_COMPLETE,
        IS_RESPONSIBLES_AND_CONTRACTORS_DATA_COMPLETE,
        IS_REPORT_READY_TO_PRESENT
    } = useCarcinogenicTabStates();

    const selectedCarcinogenicReportId = useAppSelector(
        (state) => state?.carcinogenicReport?.selectedCarcinogenicReportId
    );
    const storeCurrentTab = useAppSelector((state) => state?.carcinogenicReport?.currentTab);
    const { data: carcinogenicParametricData, isFetching: isFetchingCarcinogenicParametricData } =
        useGetCarcinogenicParametricDataQuery({});

    const handleTabChange: HandleTabChange = (
        eventOrValue: React.SyntheticEvent | TAB_INDEX,
        maybeValue?: TAB_INDEX
    ) => {
        const newValue = typeof eventOrValue === 'number' ? eventOrValue : maybeValue!;
        dispatch(setCarcinogenicReportCurrentTab(newValue));
    };
    
    const { t } = useTranslation('carcinogenicReport');
    const { translate } = useTranslate(t);

    const { showCancelCarcinogenicReportAlert } = useAlerts({ translate: translate });
    const { isFetchingCarcinogenicReportInitialData } = useCarcinogenicReportPrecharge(
        selectedCarcinogenicReportId
    );

    useEffect(() => {
        dispatch(setCarcinogenicReporParametricData(carcinogenicParametricData?.data));
    }, [carcinogenicParametricData]);

    useEffect(() => {
        if (ciiuCodes?.data?.codigos) {
            dispatch(setCarcinogenicReportCIIUCodes(ciiuCodes));
        }
    }, [ciiuCodes]);

    useLayoutEffect(() => {
        dispatch(setCarcinogenicReportCurrentTab(TAB_INDEX.ESTABLISHMENT_DATA));
    }, []);

    return (
        <>
            <Grid display="flex" direction={'row'} bgcolor={'#F3F8F8'} mt={2}>
                <Tabs
                    value={storeCurrentTab}
                    onChange={handleTabChange}
                    sx={{
                        '& .MuiTabs-indicator': {
                            top: 0,
                            bottom: 'unset',
                            height: '3px',
                            backgroundColor: (theme) => theme?.palette?.primary?.main || 'gray',
                            borderTopLeftRadius: '6px',
                            borderTopRightRadius: '6px',
                            display: 'flex',
                            width: '100%'
                        }
                    }}
                >
                    <CarcinogenicReportTab
                        label={translate('tabNames.establishmentData')}
                        value={TAB_INDEX.ESTABLISHMENT_DATA}
                        active={TAB_INDEX.ESTABLISHMENT_DATA === storeCurrentTab}
                        complete={IS_ESTABLISHMENT_DATA_COMPLETE}
                        onlyVisual={onlyVisual}
                    />
                    <CarcinogenicReportTab
                        label={translate('tabNames.substancesAndPreventiveMeasures')}
                        value={TAB_INDEX.SUBSTANCES}
                        active={TAB_INDEX.SUBSTANCES === storeCurrentTab}
                        complete={IS_SUBSTANCES_DATA_COMPLETE}
                        disabled={!IS_ESTABLISHMENT_DATA_COMPLETE}
                        onlyVisual={onlyVisual}
                    />
                    <CarcinogenicReportTab
                        label={translate('tabNames.providersAndBuyers')}
                        value={TAB_INDEX.PROVIDERS_AND_BUYERS}
                        active={TAB_INDEX.PROVIDERS_AND_BUYERS === storeCurrentTab}
                        complete={IS_PROVIDERS_AND_BUYERS_DATA_COMPLETE}
                        disabled={!IS_ESTABLISHMENT_DATA_COMPLETE || !IS_SUBSTANCES_DATA_COMPLETE}
                        onlyVisual={onlyVisual}
                    />
                    <CarcinogenicReportTab
                        label={translate('tabNames.radiologicalEquipment')}
                        value={TAB_INDEX.RADIOLOGICAL_EQUIPMENT}
                        active={TAB_INDEX.RADIOLOGICAL_EQUIPMENT === storeCurrentTab}
                        complete={IS_RADIOLOGICAL_EQUIPMENT_DATA_COMPLETE}
                        disabled={!IS_ESTABLISHMENT_DATA_COMPLETE || !IS_SUBSTANCES_DATA_COMPLETE}
                        onlyVisual={onlyVisual}
                    />
                    <CarcinogenicReportTab
                        label={translate('tabNames.workersAndMedicalExams')}
                        value={TAB_INDEX.WORKERS_AND_MEDICAL_EXAMS}
                        active={TAB_INDEX.WORKERS_AND_MEDICAL_EXAMS === storeCurrentTab}
                        complete={IS_WORKERS_AND_MEDICAL_EXAMS_DATA_COMPLETE}
                        disabled={!IS_ESTABLISHMENT_DATA_COMPLETE || !IS_SUBSTANCES_DATA_COMPLETE}
                        onlyVisual={onlyVisual}
                    />
                    <CarcinogenicReportTab
                        label={translate('tabNames.responsiblesAndContractors')}
                        value={TAB_INDEX.RESPONSIBLES_AND_CONTRACTORS}
                        active={TAB_INDEX.RESPONSIBLES_AND_CONTRACTORS === storeCurrentTab}
                        complete={IS_RESPONSIBLES_AND_CONTRACTORS_DATA_COMPLETE}
                        disabled={!IS_ESTABLISHMENT_DATA_COMPLETE || !IS_SUBSTANCES_DATA_COMPLETE}
                        onlyVisual={onlyVisual}
                    />
                </Tabs>
            </Grid>
            <Grid>
                <TabPanel value={storeCurrentTab} index={TAB_INDEX.ESTABLISHMENT_DATA}>
                    <EstablishmentDataTabContent
                        handleTabChange={handleTabChange}
                        showCancelCarcinogenicReportAlert={showCancelCarcinogenicReportAlert}
                        onlyVisual={onlyVisual}
                    />
                </TabPanel>
                <TabPanel value={storeCurrentTab} index={TAB_INDEX.SUBSTANCES}>
                    <SubstancesAndPreventiveMeasuresTabContent
                        handleTabChange={handleTabChange}
                        showCancelCarcinogenicReportAlert={showCancelCarcinogenicReportAlert}
                        allowNext={IS_ESTABLISHMENT_DATA_COMPLETE && IS_SUBSTANCES_DATA_COMPLETE}
                        onlyVisual={onlyVisual}
                    />
                </TabPanel>
                <TabPanel value={storeCurrentTab} index={TAB_INDEX.PROVIDERS_AND_BUYERS}>
                    <ProvidersTabContent
                        handleTabChange={handleTabChange}
                        showCancelCarcinogenicReportAlert={showCancelCarcinogenicReportAlert}
                        onlyVisual={onlyVisual}
                    />
                </TabPanel>
                <TabPanel value={storeCurrentTab} index={TAB_INDEX.RADIOLOGICAL_EQUIPMENT}>
                    <RadiologicalEquipmentTabContent
                        handleTabChange={handleTabChange}
                        showCancelCarcinogenicReportAlert={showCancelCarcinogenicReportAlert}
                        onlyVisual={onlyVisual}
                    />
                </TabPanel>
                <TabPanel value={storeCurrentTab} index={TAB_INDEX.WORKERS_AND_MEDICAL_EXAMS}>
                    <WorkersAndMedicalExaminationsTabContent
                        handleTabChange={handleTabChange}
                        showCancelCarcinogenicReportAlert={showCancelCarcinogenicReportAlert}
                        onlyVisual={onlyVisual}
                    />
                </TabPanel>
                <TabPanel value={storeCurrentTab} index={TAB_INDEX.RESPONSIBLES_AND_CONTRACTORS}>
                    <ResponsiblePartiesAndContractorsTabContent
                        handleTabChange={handleTabChange}
                        showCancelCarcinogenicReportAlert={showCancelCarcinogenicReportAlert}
                        IS_REPORT_READY_TO_PRESENT={IS_REPORT_READY_TO_PRESENT}
                        onlyVisual={onlyVisual}
                    />
                </TabPanel>
            </Grid>
            <BackdropComponent
                fetching={
                    isFetchingCarcinogenicReportInitialData || isFetchingCarcinogenicParametricData
                }
            />
        </>
    );
};

export default CarcinogenicReport;
